
import { useState } from 'react';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';

const ROICalculatorPage = () => {
  const [formData, setFormData] = useState({
    company: '',
    email: '',
    monthly_revenue: '',
    marketing_budget: '',
    current_conversion: '',
    lead_volume: '',
    customer_value: '',
    sales_cycle: ''
  });
  
  const [results, setResults] = useState<any>(null);
  const [showResults, setShowResults] = useState(false);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const calculateROI = () => {
    const monthlyRevenue = parseFloat(formData.monthly_revenue) || 0;
    const marketingBudget = parseFloat(formData.marketing_budget) || 0;
    const currentConversion = parseFloat(formData.current_conversion) || 0;
    const leadVolume = parseFloat(formData.lead_volume) || 0;
    const customerValue = parseFloat(formData.customer_value) || 0;
    const salesCycle = parseFloat(formData.sales_cycle) || 1;

    // AI improvement estimates based on industry averages
    const aiImprovements = {
      conversionIncrease: 0.25, // 25% average conversion improvement
      leadIncrease: 0.40, // 40% average lead increase
      efficiencyGain: 0.30, // 30% efficiency improvement
      costReduction: 0.20 // 20% cost reduction
    };

    // Current metrics
    const currentLeads = leadVolume;
    const currentConversions = currentLeads * (currentConversion / 100);
    const currentRevenue = currentConversions * customerValue;

    // Projected improvements with AI
    const newConversionRate = currentConversion * (1 + aiImprovements.conversionIncrease);
    const newLeadVolume = currentLeads * (1 + aiImprovements.leadIncrease);
    const newConversions = newLeadVolume * (newConversionRate / 100);
    const newRevenue = newConversions * customerValue;

    // Cost savings
    const currentMarketingCost = marketingBudget;
    const newMarketingCost = currentMarketingCost * (1 - aiImprovements.costReduction);
    const costSavings = currentMarketingCost - newMarketingCost;

    // ROI calculations
    const revenueIncrease = newRevenue - currentRevenue;
    const totalBenefit = revenueIncrease + costSavings;
    const aiImplementationCost = marketingBudget * 0.15; // Estimated 15% of marketing budget
    const netBenefit = totalBenefit - aiImplementationCost;
    const roiPercentage = (netBenefit / aiImplementationCost) * 100;

    // Annual projections
    const annualRevenueIncrease = revenueIncrease * 12;
    const annualCostSavings = costSavings * 12;
    const annualNetBenefit = netBenefit * 12;

    return {
      current: {
        leads: currentLeads,
        conversions: currentConversions,
        revenue: currentRevenue,
        conversionRate: currentConversion,
        marketingCost: currentMarketingCost
      },
      projected: {
        leads: newLeadVolume,
        conversions: newConversions,
        revenue: newRevenue,
        conversionRate: newConversionRate,
        marketingCost: newMarketingCost
      },
      improvements: {
        revenueIncrease,
        costSavings,
        totalBenefit,
        netBenefit,
        roiPercentage,
        implementationCost: aiImplementationCost
      },
      annual: {
        revenueIncrease: annualRevenueIncrease,
        costSavings: annualCostSavings,
        netBenefit: annualNetBenefit
      }
    };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const calculatedResults = calculateROI();
    setResults(calculatedResults);
    
    try {
      await fetch('https://readdy.ai/api/form/d44s6qn0vhksjg8rfn30', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData).toString()
      });
    } catch (error) {
      console.error('Form submission error:', error);
    }
    
    setShowResults(true);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatPercentage = (percentage: number) => {
    return `${percentage.toFixed(1)}%`;
  };

  if (showResults && results) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        
        <section className="py-20">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h1 className="text-4xl font-bold text-gray-900 mb-4">Your AI Marketing ROI Projection</h1>
              <p className="text-xl text-gray-600">
                Based on your business metrics, here's what AI marketing could deliver for your company.
              </p>
            </div>
            
            {/* Key Metrics Cards */}
            <div className="grid md:grid-cols-3 gap-6 mb-12">
              <div className="bg-gradient-to-r from-green-50 to-green-100 p-6 rounded-xl">
                <div className="text-center">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    {formatPercentage(results.improvements.roiPercentage)}
                  </div>
                  <div className="text-lg font-semibold text-gray-900">Projected ROI</div>
                  <div className="text-sm text-gray-600">Return on AI Investment</div>
                </div>
              </div>
              
              <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-6 rounded-xl">
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600 mb-2">
                    {formatCurrency(results.annual.revenueIncrease)}
                  </div>
                  <div className="text-lg font-semibold text-gray-900">Annual Revenue Increase</div>
                  <div className="text-sm text-gray-600">Additional yearly revenue</div>
                </div>
              </div>
              
              <div className="bg-gradient-to-r from-purple-50 to-purple-100 p-6 rounded-xl">
                <div className="text-center">
                  <div className="text-3xl font-bold text-purple-600 mb-2">
                    {formatCurrency(results.annual.costSavings)}
                  </div>
                  <div className="text-lg font-semibold text-gray-900">Annual Cost Savings</div>
                  <div className="text-sm text-gray-600">Marketing efficiency gains</div>
                </div>
              </div>
            </div>
            
            {/* Detailed Comparison */}
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              {/* Current Performance */}
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Current Performance</h3>
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Monthly Leads:</span>
                    <span className="font-semibold">{results.current.leads.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Conversion Rate:</span>
                    <span className="font-semibold">{formatPercentage(results.current.conversionRate)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Monthly Conversions:</span>
                    <span className="font-semibold">{results.current.conversions.toFixed(0)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Monthly Revenue:</span>
                    <span className="font-semibold">{formatCurrency(results.current.revenue)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Marketing Cost:</span>
                    <span className="font-semibold">{formatCurrency(results.current.marketingCost)}</span>
                  </div>
                </div>
              </div>
              
              {/* Projected with AI */}
              <div className="bg-gradient-to-br from-blue-50 to-indigo-100 rounded-xl shadow-lg p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Projected with AI</h3>
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Monthly Leads:</span>
                    <span className="font-semibold text-green-600">
                      {results.projected.leads.toLocaleString()} 
                      <span className="text-sm ml-1">(+40%)</span>
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Conversion Rate:</span>
                    <span className="font-semibold text-green-600">
                      {formatPercentage(results.projected.conversionRate)}
                      <span className="text-sm ml-1">(+25%)</span>
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Monthly Conversions:</span>
                    <span className="font-semibold text-green-600">{results.projected.conversions.toFixed(0)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Monthly Revenue:</span>
                    <span className="font-semibold text-green-600">{formatCurrency(results.projected.revenue)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Marketing Cost:</span>
                    <span className="font-semibold text-green-600">
                      {formatCurrency(results.projected.marketingCost)}
                      <span className="text-sm ml-1">(-20%)</span>
                    </span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Implementation Timeline */}
            <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Implementation Roadmap</h3>
              <div className="grid md:grid-cols-4 gap-6">
                <div className="text-center">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-blue-600 font-bold">1</span>
                  </div>
                  <h4 className="font-semibold text-gray-900 mb-2">Month 1-2</h4>
                  <p className="text-sm text-gray-600">AI Strategy & Setup</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-green-600 font-bold">2</span>
                  </div>
                  <h4 className="font-semibold text-gray-900 mb-2">Month 3-4</h4>
                  <p className="text-sm text-gray-600">Implementation & Testing</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-yellow-600 font-bold">3</span>
                  </div>
                  <h4 className="font-semibold text-gray-900 mb-2">Month 5-6</h4>
                  <p className="text-sm text-gray-600">Optimization & Scaling</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-purple-600 font-bold">4</span>
                  </div>
                  <h4 className="font-semibold text-gray-900 mb-2">Month 7+</h4>
                  <p className="text-sm text-gray-600">Full ROI Realization</p>
                </div>
              </div>
            </div>
            
            {/* CTA Section */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl p-8 text-center text-white">
              <h3 className="text-2xl font-bold mb-4">Ready to Achieve These Results?</h3>
              <p className="text-lg mb-6 opacity-90">
                Schedule a free consultation to discuss your AI marketing implementation strategy.
              </p>
              <button className="px-8 py-3 bg-white text-blue-600 font-semibold rounded-lg hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap">
                Schedule Free Consultation
              </button>
            </div>
          </div>
        </section>
        
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">AI Marketing ROI Calculator</h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Calculate the potential return on investment from implementing AI marketing solutions in your business.
            </p>
          </div>
          
          <form onSubmit={handleSubmit} data-readdy-form className="bg-white rounded-xl shadow-lg p-8">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Company Name</label>
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={(e) => handleInputChange('company', e.target.value)}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Monthly Revenue ($)</label>
                <input
                  type="number"
                  name="monthly_revenue"
                  value={formData.monthly_revenue}
                  onChange={(e) => handleInputChange('monthly_revenue', e.target.value)}
                  required
                  min="0"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Monthly Marketing Budget ($)</label>
                <input
                  type="number"
                  name="marketing_budget"
                  value={formData.marketing_budget}
                  onChange={(e) => handleInputChange('marketing_budget', e.target.value)}
                  required
                  min="0"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Current Conversion Rate (%)</label>
                <input
                  type="number"
                  name="current_conversion"
                  value={formData.current_conversion}
                  onChange={(e) => handleInputChange('current_conversion', e.target.value)}
                  required
                  min="0"
                  max="100"
                  step="0.1"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Monthly Lead Volume</label>
                <input
                  type="number"
                  name="lead_volume"
                  value={formData.lead_volume}
                  onChange={(e) => handleInputChange('lead_volume', e.target.value)}
                  required
                  min="0"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Average Customer Value ($)</label>
                <input
                  type="number"
                  name="customer_value"
                  value={formData.customer_value}
                  onChange={(e) => handleInputChange('customer_value', e.target.value)}
                  required
                  min="0"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Sales Cycle (months)</label>
                <input
                  type="number"
                  name="sales_cycle"
                  value={formData.sales_cycle}
                  onChange={(e) => handleInputChange('sales_cycle', e.target.value)}
                  required
                  min="0.1"
                  step="0.1"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            
            <div className="mt-8 text-center">
              <button
                type="submit"
                className="px-8 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                Calculate My ROI
              </button>
            </div>
          </form>
          
          {/* Benefits Preview */}
          <div className="mt-12 bg-gray-50 rounded-xl p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">What AI Marketing Can Deliver</h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-arrow-up-line text-2xl text-green-600"></i>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">25% Higher Conversions</h4>
                <p className="text-gray-600 text-sm">AI-powered personalization and optimization</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-user-add-line text-2xl text-blue-600"></i>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">40% More Leads</h4>
                <p className="text-gray-600 text-sm">Automated lead generation and nurturing</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-money-dollar-circle-line text-2xl text-purple-600"></i>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">20% Cost Reduction</h4>
                <p className="text-gray-600 text-sm">Improved efficiency and automation</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default ROICalculatorPage;
